import { Badge, Flex, HStack, Text } from '@chakra-ui/react'
import { Separator as Divider } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import {
  Calendar,
  Check,
  ClipboardClock,
  Minus,
  ShieldPlus,
  User,
  Wallet,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { isElectron } from '~/utils/common'
import {
  buildPartnerDeepLink,
  canBuildPartnerDeepLink,
  getContactDisplayId,
  getContactExternalIdForDeeplink,
  getContactFullName,
  getContactPatientDisplayId,
  getContactPreferredName,
  getContactStatus,
  isV2Contact,
} from '~/utils/phoneHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import {
  Appointment,
  AppointmentList,
  BalanceList,
  CallQueueItem,
  InsuranceList,
  PhoneCall,
  PopupQueueItemTypes,
} from '@mangovoice/types'

import { patientInfoButtonBehaviorAtom } from '../../atoms/PatientInfoSettings'
import { useAnalytics } from '../../hooks/useAnalytics'
import {
  usePartnerConfig,
  usePartnerExternalIdField,
} from '../../hooks/useCurrentPartner'
import { useMainWindow } from '../../hooks/useElectronWindow'
import { useSharedVariable } from '../../hooks/useSharedVariable'
import { Size } from '../../types/ThemeTypes'
import { CallButtons } from './CallButtons'
import { CallItemHeader } from './CallItemHeader'

interface PersonCardProps {
  callItem?: CallQueueItem
  onCollapse?: () => void
}

interface DataRowProps {
  icon?: React.ComponentType<any>
  label: string
  value: string | number | undefined
  rightAlign?: boolean
  statusBadge?: React.ReactNode
}

const DataRow = ({
  icon: Icon,
  label,
  value,
  rightAlign = false,
  statusBadge,
}: DataRowProps) => {
  // Hide the entire row if there's no value
  if (!value) return null

  return (
    <HStack justify="space-between" gap={2}>
      <HStack gap={2} flex={1} color="fg.muted">
        {Icon && <Icon size={16} color="var(--chakra-colors-fg-muted)" />}
        <Text {...textStyles[textStyleKeys.Body3]}>{label}</Text>
        {statusBadge}
      </HStack>
      <Text
        {...textStyles[textStyleKeys.Body3]}
        color="fg.default"
        textAlign={rightAlign ? 'right' : 'left'}
        fontWeight="semibold"
      >
        {value}
      </Text>
    </HStack>
  )
}

const formatAge = (dateOfBirth: string, age?: number) => {
  // Extract just the date part (YYYY-MM-DD) ignoring any time/timezone info
  const dateMatch = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!dateMatch) {
    return undefined
  }

  const [, year, month, day] = dateMatch
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  const formattedDate = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
  return age ? `${formattedDate} (${age} years)` : formattedDate
}

const formatAppointmentDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

// Helper functions to extract values from integration_data structure
const getInsurance = (insurance?: InsuranceList): string | undefined => {
  if (!insurance) return undefined
  const firstKey = Object.keys(insurance)[0]
  return firstKey ? insurance[firstKey]?.insurance_name : undefined
}

const getBalance = (balance?: BalanceList): string | number | undefined => {
  if (!balance) return undefined
  const firstKey = Object.keys(balance)[0]
  return firstKey ? balance[firstKey]?.balance : undefined
}

const parseNumericBalance = (balance: string | number | undefined): number => {
  if (typeof balance === 'number') {
    return balance
  }

  if (typeof balance === 'string') {
    const trimmedBalance = balance.trim()
    if (trimmedBalance.length > 0) {
      return Number(trimmedBalance)
    }
  }

  return NaN
}

const getNextAppointment = (
  appointments?: AppointmentList,
): Appointment | undefined => {
  if (!appointments) return undefined
  const now = new Date()
  const futureAppointments = Object.values(appointments).filter(
    (apt) => new Date(apt.start_time) > now,
  )
  if (futureAppointments.length === 0) return undefined
  // Sort by start date and return the earliest future appointment
  futureAppointments.sort(
    (a, b) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
  )
  return futureAppointments[0]
}

const getPreviousAppointment = (
  appointments?: AppointmentList,
): Appointment | undefined => {
  if (!appointments) return undefined

  const now = new Date()
  const pastAppointments = Object.values(appointments)
    .map((appointment) => {
      const startTime = new Date(appointment.start_time)
      const endTime = new Date(appointment.end_time)

      return {
        appointment,
        startTime,
        endTime,
      }
    })
    // Include completed and in-progress appointments.
    .filter((item) => item.startTime <= now)

  if (pastAppointments.length === 0) return undefined

  // Use end time for completed appointments, start time for in-progress.
  pastAppointments.sort((a, b) => {
    const aComparisonTime =
      a.endTime <= now ? a.endTime.getTime() : a.startTime.getTime()
    const bComparisonTime =
      b.endTime <= now ? b.endTime.getTime() : b.startTime.getTime()
    return bComparisonTime - aComparisonTime
  })

  return pastAppointments[0].appointment
}

export const PersonCard = ({ callItem, onCollapse }: PersonCardProps) => {
  const { t } = useTranslation()
  const partnerConfig = usePartnerConfig()
  const partnerExternalIdField = usePartnerExternalIdField()
  const [patientInfoBehavior] = useAtom(patientInfoButtonBehaviorAtom)
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const { answerPhoneCall } = useMainWindow()

  // Get the most up-to-date call data from callList (matching PopupPage)
  const [sharedCallList] = useSharedVariable<PhoneCall[]>('CallList')
  const currentQueueItem = (
    sharedCallList?.find((call) => call.callUuid === callItem?._id)
      ? ({
          ...sharedCallList.find((call) => call.callUuid === callItem?._id),
          _id: callItem?._id,
          type: PopupQueueItemTypes.CALL,
        } as CallQueueItem)
      : callItem
  ) as CallQueueItem

  // Use contactInfo from the fresh call data, not the stale prop
  const contactInfo = currentQueueItem?.contactInfo
  const contactExternalId = getContactExternalIdForDeeplink(
    contactInfo,
    partnerExternalIdField,
  )
  const contactDisplayId = getContactDisplayId(
    contactInfo,
    partnerExternalIdField,
  )
  const patientDisplayId = getContactPatientDisplayId(contactInfo)
  const callerNumber = currentQueueItem?.number
  const canDeepLink = canBuildPartnerDeepLink(partnerConfig.deepLinkPattern, {
    contactId: contactExternalId,
    callerNumber,
    patientDisplayId,
  })
  const integrationData = isV2Contact(contactInfo)
    ? contactInfo.integration_data
    : undefined
  const nextAppointment = getNextAppointment(integrationData?.appointment)
  const previousAppointment = getPreviousAppointment(
    integrationData?.appointment,
  )
  const balance = getBalance(integrationData?.balance)
  const numericBalance = parseNumericBalance(balance)
  const hasDisplayableBalance = Number.isFinite(numericBalance)
  const formattedBalance = hasDisplayableBalance
    ? t('global.currency.default', { value: numericBalance })
    : undefined
  const contactStatus = getContactStatus(contactInfo)

  function handlePartnerAction(): void {
    if (canDeepLink && isElectron) {
      track(WebPhoneAnalyticsEvents.PopupPatientDeepLinkClicked)

      if (patientInfoBehavior === 'answer-then-deeplink') {
        answerPhoneCall()
      }

      const url = buildPartnerDeepLink(partnerConfig.deepLinkPattern, {
        contactId: contactExternalId,
        callerNumber,
        patientDisplayId,
      })
      if (url) {
        window.electron?.send('open-external-url', url)
      }
    }
  }

  return (
    <Flex
      flexDirection={'column'}
      border="1px solid"
      borderColor="brand.focusRing"
      borderRadius="md"
      p={4}
      bg="brand.subtle"
      gap={3}
    >
      <CallItemHeader
        callItem={currentQueueItem}
        isExpanded={true}
        onToggle={onCollapse}
      />

      {canDeepLink && (
        <MangoButton
          key="patient-file"
          leftIcon={User}
          variant={MangoButtonVariant.Brand}
          onClick={() => handlePartnerAction()}
          size={Size.md}
          mt={0}
        >
          {t('popup.personCard.patientFile')}
        </MangoButton>
      )}

      {contactInfo && (
        <Flex direction="column" gap={3}>
          {getContactPreferredName(contactInfo) && (
            <HStack gap={2}>
              <Text {...textStyles[textStyleKeys.H4]} color="fg.muted">
                "{getContactPreferredName(contactInfo)}"
              </Text>
              {contactStatus && (
                <Badge
                  colorPalette={
                    contactStatus.toLowerCase() === 'active' ? 'green' : 'gray'
                  }
                  variant="subtle"
                  fontSize="xs"
                  px={1}
                  py={0.5}
                  borderRadius="md"
                  border="1px solid"
                >
                  {contactStatus}
                </Badge>
              )}
            </HStack>
          )}

          <Flex direction="column" gap={2}>
            <DataRow
              label={t('popup.personCard.fullName')}
              value={getContactFullName(contactInfo)}
            />
            <DataRow
              label={t('popup.personCard.dateOfBirth')}
              value={
                isV2Contact(contactInfo) && contactInfo.date_of_birth
                  ? formatAge(contactInfo.date_of_birth)
                  : undefined
              }
            />
            <DataRow
              label={t('popup.personCard.patientNumber')}
              value={contactDisplayId}
            />

            <DataRow
              label={t('popup.personCard.inboundNumber')}
              value={callItem?.destinationNumber}
            />
            {currentQueueItem?.extensionNumber && (
              <DataRow
                label={t('popup.personCard.extension')}
                value={
                  currentQueueItem.extensionName
                    ? `${currentQueueItem.extensionName} (${currentQueueItem.extensionNumber})`
                    : currentQueueItem.extensionNumber
                }
              />
            )}

            <DataRow
              label={t('popup.personCard.defaultLocation')}
              value={
                isV2Contact(contactInfo)
                  ? contactInfo.default_location
                  : undefined
              }
            />
            <Divider borderColor="border.emphasized" my={2} />
            <DataRow
              icon={ClipboardClock}
              label={t('popup.personCard.nextAppointment')}
              value={
                nextAppointment?.start_time
                  ? formatAppointmentDate(nextAppointment.start_time)
                  : undefined
              }
              statusBadge={
                nextAppointment?.status ? (
                  <Flex
                    align="center"
                    justify="center"
                    bg={
                      nextAppointment.status.toLowerCase() === 'confirmed'
                        ? 'green.muted'
                        : 'bg.muted'
                    }
                    borderRadius="4px"
                    w="16px"
                    h="16px"
                  >
                    {nextAppointment.status.toLowerCase() === 'confirmed' ? (
                      <Check size={12} strokeWidth={3} />
                    ) : (
                      <Minus size={12} strokeWidth={3} />
                    )}
                  </Flex>
                ) : undefined
              }
            />
            <DataRow
              icon={Calendar}
              label={t('popup.personCard.previousAppointment')}
              value={
                previousAppointment?.start_time
                  ? formatAppointmentDate(previousAppointment.start_time)
                  : undefined
              }
            />
            <DataRow
              icon={ShieldPlus}
              label={t('popup.personCard.insurance', {
                defaultValue: 'Insurance',
              })}
              value={getInsurance(integrationData?.insurance)}
            />
            <DataRow
              icon={Wallet}
              label={t('popup.personCard.balance')}
              value={formattedBalance}
              rightAlign={true}
            />
          </Flex>
        </Flex>
      )}
      {callItem && <CallButtons callItem={callItem} />}
    </Flex>
  )
}
